'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import type { MergeMethod } from './types';
import { readStoredMergeMethod, writeStoredMergeMethod } from './merge-method-preference';
type UseMergeMethodPreferenceParams = {
  owner: string;
  repo: string;
};

/**
 * Reads the stored merge method preference after mount.
 *
 * Deliberately starts as null and hydrates in an effect: reading localStorage
 * during render would produce a server/client mismatch on the SSR'd PR page.
 * Until it hydrates, callers fall back to the derived default, so the button
 * label never flickers between two *allowed* methods on a fresh page load.
 */
export function useMergeMethodPreference(t0) {
  const $ = _c(10);
  const {
    owner,
    repo
  } = t0;
  const [preferredMergeMethod, setPreferredMergeMethod] = useState(null);
  let t1;
  let t2;
  if ($[0] !== owner || $[1] !== repo) {
    t1 = () => {
      setPreferredMergeMethod(readStoredMergeMethod({
        owner,
        repo
      }));
    };
    t2 = [owner, repo];
    $[0] = owner;
    $[1] = repo;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== owner || $[5] !== repo) {
    t3 = method => {
      setPreferredMergeMethod(method);
      writeStoredMergeMethod({
        owner,
        repo
      }, method);
    };
    $[4] = owner;
    $[5] = repo;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const selectMergeMethod = t3;
  let t4;
  if ($[7] !== preferredMergeMethod || $[8] !== selectMergeMethod) {
    t4 = {
      preferredMergeMethod,
      selectMergeMethod
    };
    $[7] = preferredMergeMethod;
    $[8] = selectMergeMethod;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}